import React from 'react';
import { cloneDeep, isNull } from 'lodash';

import { Nullable } from '@tager/web-core';

import {
  Filter,
  FilteredFields,
  FilteredValueField,
  FilterTypes,
  HandleChange,
} from '@/typings/filter/types';
import {
  Value,
  Values,
} from '@/store/reducers/pages/filters/CatalogProductFilters/filters';

import Range from './components/Range';
import Radio from './components/Radio';
import TrueFalse from './components/TrueFalse';
import MultiSelect from './components/MultiSelect';

export const getFields = (
  field: Filter,
  value: Value['data'],
  onChange: HandleChange
) => {
  switch (field.type) {
    case FilterTypes.Range:
      return (
        <Range field={field} value={value as number[]} onChange={onChange} />
      );

    case FilterTypes.Radio:
      return <Radio field={field} />;

    case FilterTypes.TrueFalse:
      return (
        <TrueFalse
          field={field}
          value={value as Nullable<boolean>}
          onChange={onChange}
        />
      );

    case FilterTypes.MultiSelect:
      return (
        <MultiSelect
          field={field}
          value={value as string[]}
          onChange={onChange}
        />
      );

    default:
      return [];
  }
};

export const convertFilterFieldsToState = (
  data: Filter[]
): FilteredFields[] => {
  return data.reduce<any[]>((filter, field) => {
    switch (field.type) {
      case FilterTypes.Range:
        filter.push({
          ...field,
          value: [field.valueFrom, field.valueTo],
        });
        return filter;
      case FilterTypes.MultiSelect:
        const options: string[] = [];
        field.options.forEach((option, index) => {
          if (option.checked) {
            options.push(option.id);
          }
        });
        filter.push({
          ...field,
          value: options,
        });
        return filter;
      case FilterTypes.TrueFalse:
        filter.push({
          ...field,
          value: null,
        });
        return filter;
      case FilterTypes.Radio:
        filter.push({
          ...field,
          value: null,
        });
        return filter;
      default:
        return filter;
    }
  }, []);
};


export type MultiselectOrTrueFalseValue = { label: string; value: boolean };

export type Payload = {
  id: string;
  filterType: string;
  value: Nullable<number[] | boolean | MultiselectOrTrueFalseValue>;
};


const getFilterKey = (key: string) => `filters[${key}]`;

export const convertValuesToString = (values: Values) => {
  return Object.keys(values).reduce<
    Record<string, string | string[] | number[] | Nullable<boolean>>
  >((queryObject, key) => {
    switch (values[key].type) {
      case FilterTypes.Range:
        return (values[key].data as number[]).length === 0
          ? queryObject
          : {
              ...queryObject,
              [getFilterKey(key)]: (values[key].data as string[]).join(':'),
            };
      case FilterTypes.MultiSelect:
        return values[key].data && (values[key].data as string[]).length === 0
          ? queryObject
          : {
              ...queryObject,
              [getFilterKey(key)]: (values[key].data as string[]).join(','),
            };
      case FilterTypes.TrueFalse:
        return values[key].data === null
          ? queryObject
          : {
              ...queryObject,
              [getFilterKey(key)]: values[key].data,
            };
      case FilterTypes.Radio:
        return {
          ...queryObject,
          [getFilterKey(key)]: values[key].data,
        };
      default:
        return queryObject;
    }
  }, {});
};
